import type { Language } from '../stores/app-store';

interface FlagIconProps {
    language: Language;
    className?: string;
}

// SVG flag components for languages without standard emoji flags
const KurdistanFlag = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
        {/* Kurdistan KRG flag - Red, White, Green horizontal stripes with sun */}
        <rect width="900" height="200" fill="#EE2A35" />
        <rect y="200" width="900" height="200" fill="#FFFFFF" />
        <rect y="400" width="900" height="200" fill="#00843D" />
        {/* Sun in center */}
        <circle cx="450" cy="300" r="80" fill="#FFD700" />
        {/* Sun rays */}
        {Array.from({ length: 21 }, (_, i) => {
            const angle = (i * 360) / 21;
            const rad = (angle * Math.PI) / 180;
            const x1 = 450 + Math.cos(rad) * 80;
            const y1 = 300 + Math.sin(rad) * 80;
            const x2 = 450 + Math.cos(rad) * 100;
            const y2 = 300 + Math.sin(rad) * 100;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#FFD700" strokeWidth="3" />;
        })}
    </svg>
);

const IranLionSunFlag = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
        {/* Historical Iranian flag - Green, White, Red horizontal stripes */}
        <rect width="900" height="200" fill="#239F40" />
        <rect y="200" width="900" height="200" fill="#FFFFFF" />
        <rect y="400" width="900" height="200" fill="#DA0000" />
        {/* Simplified Lion and Sun emblem in center */}
        <g transform="translate(450, 300)">
            {/* Sun */}
            <circle r="50" fill="#FFD700" />
            {/* Sun rays */}
            {Array.from({ length: 16 }, (_, i) => {
                const angle = (i * 360) / 16;
                const rad = (angle * Math.PI) / 180;
                const x1 = Math.cos(rad) * 50;
                const y1 = Math.sin(rad) * 50;
                const x2 = Math.cos(rad) * 70;
                const y2 = Math.sin(rad) * 70;
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#FFD700" strokeWidth="4" />;
            })}
            {/* Simplified lion silhouette */}
            <g transform="translate(-20, 10)">
                <ellipse cx="0" cy="0" rx="35" ry="25" fill="#8B4513" />
                <circle cx="-15" cy="-15" r="18" fill="#8B4513" />
                <path d="M -15,-25 Q -20,-35 -15,-40 Q -10,-35 -15,-25" fill="#8B4513" />
                <ellipse cx="20" cy="15" rx="8" ry="12" fill="#8B4513" />
                <path d="M 30,5 Q 45,0 50,10 L 35,15 Z" fill="#8B4513" />
                <circle cx="-18" cy="-18" r="3" fill="#000" />
            </g>
        </g>
    </svg>
);

// SVG emoji-style flags for better cross-platform rendering
const USFlag = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
        <rect width="900" height="600" fill="#B22234" />
        <rect y="46" width="900" height="46" fill="#FFFFFF" />
        <rect y="138" width="900" height="46" fill="#FFFFFF" />
        <rect y="230" width="900" height="46" fill="#FFFFFF" />
        <rect y="322" width="900" height="46" fill="#FFFFFF" />
        <rect y="414" width="900" height="46" fill="#FFFFFF" />
        <rect y="506" width="900" height="46" fill="#FFFFFF" />
        <rect width="360" height="322" fill="#3C3B6E" />
    </svg>
);

const SpainFlag = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
        <rect width="900" height="150" fill="#AA151B" />
        <rect y="150" width="900" height="300" fill="#F1BF00" />
        <rect y="450" width="900" height="150" fill="#AA151B" />
    </svg>
);

const RomaniaFlag = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
        <rect width="300" height="600" fill="#002B7F" />
        <rect x="300" width="300" height="600" fill="#FCD116" />
        <rect x="600" width="300" height="600" fill="#CE1126" />
    </svg>
);

const SaudiFlag = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
        <rect width="900" height="600" fill="#165B33" />
        <text x="450" y="320" fontSize="180" fill="#FFFFFF" textAnchor="middle" fontFamily="Arial">
            السعودية
        </text>
    </svg>
);

export function FlagIcon({ language, className = "w-6 h-4" }: FlagIconProps) {
    // Use custom SVG flags for all languages for consistent rendering
    switch (language) {
        case 'en':
            return <USFlag className={className} />;
        case 'es':
            return <SpainFlag className={className} />;
        case 'ar':
            return <SaudiFlag className={className} />;
        case 'fa':
            return <IranLionSunFlag className={className} />;
        case 'ku':
        case 'ckb':
            return <KurdistanFlag className={className} />;
        case 'ro':
            return <RomaniaFlag className={className} />;
        default:
            return <span className={className}>🏳️</span>;
    }
}
